import { useEffect, useEffectEvent, useRef, useState } from "react";

interface UseStreamedMessageProps {
  message: string;
  charCount?: number;
  charDelay?: number;
  onCompleted?: () => void;
  stream?: boolean;
  skip?: boolean;
}

export function useStreamedMessage({
  message,
  charCount = 1,
  charDelay = 5,
  onCompleted = () => {},
  stream = true,
  skip = false,
}: UseStreamedMessageProps) {
  const [revealedChars, setRevealedChars] = useState(
    stream || skip ? 0 : message.length,
  );
  const hasCompletedRef = useRef(false);

  const onCompletedEvent = useEffectEvent(() => {
    onCompleted();
  });

  useEffect(() => {
    if (skip) {
      return;
    }
    hasCompletedRef.current = false;
    const inter = setInterval(() => {
      setRevealedChars((prev: number) => {
        const next = Math.min(prev + charCount, message.length);
        if (next >= message.length) {
          clearInterval(inter);
        }
        return next;
      });
    }, charDelay);
    return () => clearInterval(inter);
  }, [skip, message, charCount, charDelay]);

  useEffect(() => {
    if (
      revealedChars > 0 &&
      revealedChars >= message.length &&
      !hasCompletedRef.current
    ) {
      hasCompletedRef.current = true;
      onCompletedEvent();
    }
  }, [revealedChars, message.length]);

  const currentMessage = message.slice(0, revealedChars);

  return currentMessage;
}
